import React, { useState, useEffect } from "react";
import { contasReceber, clientes, downloadExcel, exportData } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, Search, Download, Eye, Trash2, DollarSign, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatCurrency, formatDate } from "../../lib/utils";

export function ContasReceberPage() {
  const [data, setData] = useState([]);
  const [clientesList, setClientesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [addingRecebimento, setAddingRecebimento] = useState(false);
  const [savingRecebimento, setSavingRecebimento] = useState(false);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    cliente_id: "",
    cliente_nome: "",
    data_emissao: new Date().toISOString().split("T")[0],
    data_vencimento: "",
    valor_previsto: 0,
    observacoes: "",
  });

  const [recebimentoData, setRecebimentoData] = useState({
    valor: 0,
    data_recebimento: new Date().toISOString().split("T")[0],
    observacoes: "",
  });

  const fetchData = async () => {
    try {
      const [contasRes, clientesRes] = await Promise.all([
        contasReceber.list(),
        clientes.list(),
      ]);
      setData(contasRes.data);
      setClientesList(clientesRes.data);
    } catch (error) {
      toast.error("Erro ao carregar contas a receber");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await contasReceber.create(formData);
      toast.success("Conta a receber criada!");
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar");
    }
  };

  const handleAddRecebimento = async () => {
    if (!viewingItem) return;
    if (recebimentoData.valor <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    if (recebimentoData.valor > viewingItem.saldo_pendente) {
      toast.error(`Valor não pode exceder o saldo pendente de ${formatCurrency(viewingItem.saldo_pendente)}`);
      return;
    }

    setSavingRecebimento(true);
    try {
      const response = await contasReceber.registrarRecebimento(viewingItem.id, recebimentoData);
      toast.success("Recebimento registrado!");
      setViewingItem(response.data);
      setAddingRecebimento(false);
      setRecebimentoData({
        valor: 0,
        data_recebimento: new Date().toISOString().split("T")[0],
        observacoes: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao registrar recebimento");
    } finally {
      setSavingRecebimento(false);
    }
  };

  const handleRemoveRecebimento = async (recebimentoId) => {
    if (!viewingItem) return;
    if (!window.confirm("Deseja remover este recebimento?")) return;

    try {
      await contasReceber.removerRecebimento(viewingItem.id, recebimentoId);
      toast.success("Recebimento removido!");
      // Refresh the viewing item
      const contasRes = await contasReceber.list();
      const updated = contasRes.data.find(c => c.id === viewingItem.id);
      if (updated) setViewingItem(updated);
      fetchData();
    } catch (error) {
      toast.error("Erro ao remover recebimento");
    }
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clientesList.find((c) => c.id === clienteId);
    setFormData({
      ...formData,
      cliente_id: clienteId,
      cliente_nome: cliente?.razao_social || "",
    });
  };

  const resetForm = () => {
    setFormData({
      cliente_id: "",
      cliente_nome: "",
      data_emissao: new Date().toISOString().split("T")[0],
      data_vencimento: "",
      valor_previsto: 0,
      observacoes: "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta conta a receber?")) return;
    try {
      await contasReceber.delete(id);
      toast.success("Conta excluída!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const handleExport = async () => {
    try {
      await downloadExcel(exportData.contasReceber, "contas_receber.xlsx");
      toast.success("Exportado!");
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  const filteredData = data.filter((item) => {
    const matchesSearch =
      item.cliente_nome?.toLowerCase().includes(search.toLowerCase()) ||
      item.nf_numero?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.status_recebimento === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const columns = [
    { 
      key: "nf_numero", 
      header: "NF", 
      render: (v) => v ? (
        <span className="flex items-center gap-1 text-blue-600">
          <FileText className="h-3 w-3" />
          {v}
        </span>
      ) : <span className="text-muted-foreground">-</span>
    },
    { key: "cliente_nome", header: "Cliente" },
    { key: "data_vencimento", header: "Vencimento", render: (v) => formatDate(v) },
    { key: "valor_previsto", header: "Valor Total", render: (v) => formatCurrency(v) },
    { key: "valor_recebido", header: "Recebido", render: (v) => <span className="text-green-600">{formatCurrency(v)}</span> },
    { key: "saldo_pendente", header: "Saldo", render: (v) => <span className="text-orange-600 font-medium">{formatCurrency(v)}</span> },
    { key: "status_recebimento", header: "Status", render: (v) => <StatusBadge status={v} /> },
    {
      key: "actions",
      header: "",
      width: "100px",
      render: (_, row) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setViewingItem(row);
              setAddingRecebimento(false);
            }}
            data-testid={`view-conta-${row.id}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {isAdmin() && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(row.id)}
              data-testid={`delete-conta-receber-${row.id}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Calculate totals
  const totals = filteredData.reduce(
    (acc, item) => ({
      previsto: acc.previsto + (item.valor_previsto || 0),
      recebido: acc.recebido + (item.valor_recebido || 0),
      pendente: acc.pendente + (item.saldo_pendente || 0),
    }),
    { previsto: 0, recebido: 0, pendente: 0 }
  );

  return (
    <div className="space-y-6" data-testid="contas-receber-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Contas a Receber</h1>
          <p className="text-muted-foreground">Controle de recebimentos por NF</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-contas-receber-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          {isAdmin() && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-conta-receber-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Conta
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nova Conta a Receber</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <Select value={formData.cliente_id} onValueChange={handleClienteChange}>
                      <SelectTrigger data-testid="conta-receber-cliente">
                        <SelectValue placeholder="Selecione o cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientesList.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Data Emissão</Label>
                      <Input
                        type="date"
                        value={formData.data_emissao}
                        onChange={(e) => setFormData({ ...formData, data_emissao: e.target.value })}
                        data-testid="conta-receber-emissao"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Data Vencimento *</Label>
                      <Input
                        type="date"
                        value={formData.data_vencimento}
                        onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                        required
                        data-testid="conta-receber-vencimento"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Valor *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_previsto}
                      onChange={(e) => setFormData({ ...formData, valor_previsto: parseFloat(e.target.value) || 0 })}
                      required
                      data-testid="conta-receber-valor"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Observações</Label>
                    <Input
                      value={formData.observacoes}
                      onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                      data-testid="conta-receber-obs"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" data-testid="save-conta-receber-btn">Criar</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-sm border p-4">
          <p className="text-sm text-muted-foreground">Total Previsto</p>
          <p className="text-2xl font-heading font-bold">{formatCurrency(totals.previsto)}</p>
        </div>
        <div className="bg-white rounded-sm border p-4">
          <p className="text-sm text-muted-foreground">Total Recebido</p>
          <p className="text-2xl font-heading font-bold text-green-600">{formatCurrency(totals.recebido)}</p>
        </div>
        <div className="bg-white rounded-sm border p-4">
          <p className="text-sm text-muted-foreground">Saldo Pendente</p>
          <p className="text-2xl font-heading font-bold text-orange-600">{formatCurrency(totals.pendente)}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por cliente ou NF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-contas-receber"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="parcial">Parcial</SelectItem>
            <SelectItem value="recebido">Recebido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} data={filteredData} loading={loading} emptyMessage="Nenhuma conta encontrada" />

      {/* View/Manage Recebimentos Dialog */}
      <Dialog open={!!viewingItem} onOpenChange={() => { setViewingItem(null); setAddingRecebimento(false); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Gerenciar Recebimentos
            </DialogTitle>
          </DialogHeader>
          {viewingItem && (
            <div className="space-y-6">
              {/* Info da Conta */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Cliente:</span>{" "}
                    <span className="font-medium">{viewingItem.cliente_nome}</span>
                  </div>
                  {viewingItem.nf_numero && (
                    <div>
                      <span className="text-muted-foreground">NF:</span>{" "}
                      <span className="font-medium text-blue-600">{viewingItem.nf_numero}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Vencimento:</span>{" "}
                    <span className="font-medium">{formatDate(viewingItem.data_vencimento)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>{" "}
                    <StatusBadge status={viewingItem.status_recebimento} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 pt-2 border-t">
                  <div>
                    <p className="text-xs text-muted-foreground">Valor Total</p>
                    <p className="text-lg font-bold">{formatCurrency(viewingItem.valor_previsto)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Recebido</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(viewingItem.valor_recebido)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Saldo Pendente</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(viewingItem.saldo_pendente)}</p>
                  </div>
                </div>
              </div>

              {/* Histórico de Recebimentos */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Histórico de Recebimentos</h4>
                  {isAdmin() && viewingItem.status_recebimento !== "recebido" && (
                    <Button size="sm" onClick={() => setAddingRecebimento(true)} data-testid="add-recebimento-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      Novo Recebimento
                    </Button>
                  )}
                </div>

                {viewingItem.recebimentos && viewingItem.recebimentos.length > 0 ? (
                  <div className="border rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2">Data</th>
                          <th className="text-right p-2">Valor</th>
                          <th className="text-left p-2">Observações</th>
                          {isAdmin() && <th className="p-2 w-10"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {viewingItem.recebimentos.map((rec, idx) => (
                          <tr key={rec.id || idx} className="border-t">
                            <td className="p-2">{formatDate(rec.data_recebimento)}</td>
                            <td className="p-2 text-right text-green-600 font-medium">{formatCurrency(rec.valor)}</td>
                            <td className="p-2 text-muted-foreground">{rec.observacoes || "-"}</td>
                            {isAdmin() && (
                              <td className="p-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleRemoveRecebimento(rec.id)}
                                  data-testid={`remove-recebimento-${rec.id}`}
                                >
                                  <Trash2 className="h-3 w-3 text-destructive" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4 border rounded-sm">
                    Nenhum recebimento registrado
                  </p>
                )}
              </div>

              {/* Formulário de Novo Recebimento */}
              {addingRecebimento && (
                <div className="bg-green-50 p-4 rounded-lg space-y-4 border border-green-200">
                  <h4 className="font-semibold text-green-800">Registrar Novo Recebimento</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Valor *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={viewingItem.saldo_pendente}
                        value={recebimentoData.valor}
                        onChange={(e) => setRecebimentoData({ ...recebimentoData, valor: parseFloat(e.target.value) || 0 })}
                        data-testid="recebimento-valor"
                      />
                      <p className="text-xs text-muted-foreground">
                        Máximo: {formatCurrency(viewingItem.saldo_pendente)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Data *</Label>
                      <Input
                        type="date"
                        value={recebimentoData.data_recebimento}
                        onChange={(e) => setRecebimentoData({ ...recebimentoData, data_recebimento: e.target.value })}
                        data-testid="recebimento-data"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Observações</Label>
                    <Input
                      value={recebimentoData.observacoes}
                      onChange={(e) => setRecebimentoData({ ...recebimentoData, observacoes: e.target.value })}
                      placeholder="Ex: Pagamento parcial, Boleto, PIX..."
                      data-testid="recebimento-obs"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setAddingRecebimento(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleAddRecebimento} disabled={savingRecebimento} data-testid="confirm-recebimento-btn">
                      {savingRecebimento ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        "Confirmar Recebimento"
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
